/**
 * Bundle Upsell Popup — FIXED (Mar 2026)
 *
 * Correct logic:
 * - "Buying Separately" = sum of ALL individual packs inside the bundle
 * - "The Bundle" = the bundle's actual price
 * - Savings = individual total minus bundle price
 * - Upgrade cost = bundle price minus current pack price
 *
 * Data comes from Liquid-injected JSON on #bundle-upsell-modal[data-bundles],
 * NOT from parsing the CBB app's DOM (which was the source of the broken math).
 */
function initBundleUpsellPopup(config) {
  const {
    addToCartSelector = '.bundle--add-to-cart',
    enableCountdown = false,
    productTitle = ''
  } = config || {};

  // --- Session tracking (unchanged) ---
  const SESSION_SHOWN_KEY = 'graux_upsell_shown_variants';

  function getShownVariants() {
    try {
      return JSON.parse(sessionStorage.getItem(SESSION_SHOWN_KEY) || '[]');
    } catch (e) {
      return [];
    }
  }

  function markVariantShown(variantId) {
    const shown = getShownVariants();
    if (!shown.includes(variantId)) {
      shown.push(variantId);
      sessionStorage.setItem(SESSION_SHOWN_KEY, JSON.stringify(shown));
    }
  }

  function hasVariantBeenShown(variantId) {
    return getShownVariants().includes(variantId);
  }

  // --- Countdown timer (unchanged) ---
  function startCountdownTimer() {
    if (!enableCountdown) return;
    const productUrl = window.location.pathname;
    const startTimer = () => {
      if (window.startBundleCountdown) {
        window.startBundleCountdown(productUrl, productTitle);
      } else {
        setTimeout(startTimer, 100);
      }
    };
    startTimer();
  }

  function hideCountdownTimer() {
    window.hideBundleCountdown?.();
  }

  // --- DOM references ---
  const modal = document.getElementById('bundle-upsell-modal');
  if (!modal) return;

  const modalOverlay = modal.querySelector('.bundle-upsell-overlay');
  const modalClose = modal.querySelector('.bundle-upsell-close');
  const addWithBtn = document.getElementById('add-with-upsells');
  const addWithoutBtn = document.getElementById('add-without-upsells');
  const appContainer = document.getElementById('product-fb');

  // Display elements
  const titleEl = document.getElementById('bundle-upsell-title');
  const subtitleEl = document.getElementById('bundle-upsell-subtitle');
  const normalTotalEl = document.getElementById('bundle-normal-total');
  const priceBreakdownEl = document.getElementById('bundle-price-breakdown');
  const withoutProductsEl = document.getElementById('bundle-without-products');
  const withNameEl = document.getElementById('bundle-with-name');
  const withMetaEl = document.getElementById('bundle-with-meta');
  const withImageEl = document.getElementById('bundle-with-image');
  const salePriceEl = document.getElementById('bundle-sale-price');
  const originalPriceEl = document.getElementById('bundle-original-price');
  const savingsPercentEl = document.getElementById('bundle-savings-percent');
  const savingsAmountEl = document.getElementById('bundle-savings-amount');
  const savingsBadgeEl = document.getElementById('bundle-savings-badge');
  const upgradePromptEl = document.getElementById('bundle-upgrade-prompt');
  const upgradePriceEl = document.getElementById('bundle-upgrade-price');

  // --- Read pre-computed bundle data from Liquid ---
  let bundles = [];
  let currentProductPrice = 0;
  let currentProductTitle = '';

  try {
    bundles = JSON.parse(modal.getAttribute('data-bundles') || '[]');
    currentProductPrice = parseFloat(modal.getAttribute('data-current-product-price') || '0');
    currentProductTitle = modal.getAttribute('data-current-product-title') || productTitle;
  } catch (e) {
    console.error('Bundle upsell: failed to parse bundle data', e);
    return;
  }

  // No bundles available — nothing to upsell
  if (!bundles.length) return;

  /**
   * Pick the best bundle to show.
   * Prefer: highest savings percentage, then smallest upgrade cost.
   * Filter out bundles with no real savings.
   */
  function pickBestBundle() {
    const valid = bundles.filter(b => b.savingsCents > 0 && b.packs && b.packs.length > 1);
    if (!valid.length) return bundles[0] || null;

    // Sort by savings percentage descending, then upgrade cost ascending
    valid.sort((a, b) => {
      if (b.savingsPercent !== a.savingsPercent) return b.savingsPercent - a.savingsPercent;
      return a.upgradeCostCents - b.upgradeCostCents;
    });

    return valid[0];
  }

  // Track which bundle was selected for the "Get bundle" action
  let selectedBundle = null;

  /**
   * Format cents to dollar string (e.g. 3495 -> "$34.95")
   * Note: Liquid money values in cents are already divided by 100 in our JSON.
   */
  function formatMoney(dollarStr) {
    const num = parseFloat(dollarStr);
    if (isNaN(num)) return '$0.00';
    return '$' + num.toFixed(2);
  }

  function formatCents(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  /**
   * Populate the modal with the selected bundle's data.
   */
  function populateModal(bundle) {
    if (!bundle) return;
    selectedBundle = bundle;

    // --- "Buying Separately" column ---
    // List ALL packs in the bundle with their individual prices
    if (withoutProductsEl && bundle.packs) {
      let html = '';
      bundle.packs.forEach(pack => {
        const currentBadge = pack.isCurrent
          ? '<span class="current-badge">your pack</span>'
          : '';
        const nameClass = pack.isCurrent ? 'pack-name is-current' : 'pack-name';
        html += '<div class="bundle-without-product-item">' +
          '<span class="' + nameClass + '">' + pack.title + currentBadge + '</span>' +
          '<span class="pack-price">' + formatMoney(pack.price) + '</span>' +
          '</div>';
      });
      withoutProductsEl.innerHTML = html;
    }

    // Individual total
    if (normalTotalEl) {
      normalTotalEl.textContent = formatMoney(bundle.individualTotal);
    }

    // Price breakdown (e.g. "$34.95 + $34.95 + $34.95 + $34.95")
    if (priceBreakdownEl && bundle.packs) {
      const prices = bundle.packs.map(p => formatMoney(p.price));
      priceBreakdownEl.textContent = prices.join(' + ');
    }

    // --- "The Bundle" column ---
    if (withNameEl) {
      withNameEl.textContent = bundle.title;
    }

    if (withMetaEl) {
      const parts = [];
      if (bundle.packCount > 0) parts.push(bundle.packCount + ' packs');
      if (bundle.loops > 0) parts.push(bundle.loops + ' loops');
      withMetaEl.textContent = parts.join(' \u2014 ');
    }

    if (withImageEl && bundle.imageUrl) {
      withImageEl.innerHTML = '<img src="' + bundle.imageUrl + '" alt="' + (bundle.title || 'Bundle') + '" loading="lazy">';
    }

    // Bundle price
    if (salePriceEl) {
      salePriceEl.textContent = formatMoney(bundle.price);
    }

    // Strikethrough individual total (NOT compare_at_price -- show what buying separately costs)
    if (originalPriceEl) {
      originalPriceEl.textContent = formatMoney(bundle.individualTotal);
    }

    // Savings
    if (savingsPercentEl) {
      savingsPercentEl.textContent = bundle.savingsPercent;
    }
    if (savingsAmountEl) {
      savingsAmountEl.textContent = formatCents(bundle.savingsCents);
    }
    if (savingsBadgeEl) {
      savingsBadgeEl.style.display = bundle.savingsPercent > 0 ? 'inline-block' : 'none';
    }

    // Upgrade prompt
    if (upgradePromptEl) {
      const upgradeCost = bundle.upgradeCostCents / 100;
      if (upgradeCost > 0) {
        upgradePromptEl.innerHTML =
          'You\'re buying <strong>' + currentProductTitle + '</strong> (' + formatMoney(String(currentProductPrice)) + '). ' +
          'It\'s included in <strong>' + bundle.title + '</strong> (' + bundle.packCount + ' packs). ' +
          'Upgrade for just <span class="upgrade-amount">' + formatCents(bundle.upgradeCostCents) + ' more</span>.';
        upgradePromptEl.style.display = '';
      } else {
        upgradePromptEl.style.display = 'none';
      }
    }

    // CTA button text
    if (upgradePriceEl) {
      upgradePriceEl.textContent = 'Save ' + formatCents(bundle.savingsCents);
    }

    // Dynamic title
    if (titleEl) {
      if (bundle.savingsPercent > 0) {
        titleEl.textContent = 'Unlock the ' + bundle.title + ' Collection';
      }
    }

    // Dynamic subtitle
    if (subtitleEl) {
      subtitleEl.textContent = bundle.packCount + ' packs of guitar ideas instead of 1. Save ' + bundle.savingsPercent + '%. Included in the GRAUX Vault.';
    }
  }

  // --- Modal open/close logic ---

  window.openBundleUpsellPopup = function (force = false, variantId = null) {
    if (!modal) return false;

    // Check session
    if (!force && variantId && hasVariantBeenShown(variantId)) {
      return false;
    }

    // Pick best bundle
    const bundle = pickBestBundle();
    if (!bundle && !force) return false;

    // No savings = no upsell
    if (!force && bundle && bundle.savingsCents <= 0) return false;

    // Hide the CBB app's native add button
    const appAddBtn = appContainer?.querySelector('.cbb-frequently-bought-add-button');
    if (appAddBtn) appAddBtn.style.display = 'none';

    // Populate and show
    populateModal(bundle);
    modal.classList.add('active');
    document.body.style.overflow = 'hidden';

    if (variantId) {
      markVariantShown(variantId);
    }

    return true;
  };

  window.forceOpenBundleUpsellPopup = () => window.openBundleUpsellPopup(true);

  function closePopup() {
    if (!modal) return;
    modal.classList.remove('active');
    document.body.style.overflow = '';
    modal.removeAttribute('data-form-data');
    startCountdownTimer();
  }

  modalClose?.addEventListener('click', closePopup);
  modalOverlay?.addEventListener('click', closePopup);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && modal?.classList.contains('active')) {
      closePopup();
    }
  });

  // --- Intercept add-to-cart ---
  document.querySelectorAll(addToCartSelector).forEach(btn => {
    const form = btn.closest('form');
    if (!form) return;

    btn.addEventListener('click', async (e) => {
      e.preventDefault();

      const formData = new FormData(form);
      const variantId = formData.get('id');

      // Only show upsell if we have valid bundle data
      if (bundles.length > 0) {
        modal.setAttribute('data-form-data', JSON.stringify({
          id: variantId,
          quantity: formData.get('quantity') || 1
        }));

        if (!window.openBundleUpsellPopup(false, variantId)) {
          // Upsell already shown or no valid bundles — just add to cart
          await addToCartNormally(formData);
        }
      } else {
        await addToCartNormally(formData);
      }
    });
  });

  // --- Cart helpers ---

  async function waitForCartUpdate(initialItemCount, maxWaitTime = 3000) {
    const startTime = Date.now();
    const checkInterval = 200;

    return new Promise((resolve) => {
      const checkCart = async () => {
        try {
          const response = await fetch('/cart.js');
          const cart = await response.json();
          if (cart.item_count !== initialItemCount || Date.now() - startTime >= maxWaitTime) {
            resolve();
            return;
          }
          setTimeout(checkCart, checkInterval);
        } catch (error) {
          console.error('Error checking cart:', error);
          setTimeout(resolve, 1000);
        }
      };
      setTimeout(checkCart, 500);
    });
  }

  function openCartDrawer() {
    if (window.openCartDrawer) {
      window.openCartDrawer(false);
    } else if (window.grauxCart?.open) {
      window.grauxCart.open(false);
    } else {
      const drawer = document.getElementById('AjaxCartDrawer');
      if (drawer) {
        drawer.classList.add('drawer--is-open');
        document.documentElement.classList.add('js-drawer-open', 'js-cart-drawer-open');
        document.body.style.overflow = 'hidden';
      }
    }
  }

  // "Get bundle" button — add the BUNDLE (not the pack + bundle)
  addWithBtn?.addEventListener('click', async () => {
    if (!selectedBundle) return;

    closePopup();

    // First try: use the CBB app button if available (it handles bundle logic)
    const cbbBtn = appContainer?.querySelector('.cbb-frequently-bought-add-button');
    if (cbbBtn) {
      // Make sure all CBB checkboxes are checked
      const cbbContainer = document.getElementById('bundle-upsell-products');
      cbbContainer?.querySelectorAll('.cbb-frequently-bought-selector-input')
        .forEach(cb => { cb.checked = true; });

      let initialItemCount = 0;
      try {
        const cart = await fetch('/cart.js').then(r => r.json());
        initialItemCount = cart.item_count;
      } catch (e) { /* continue */ }

      cbbBtn.click();

      window.markBundleUpsellAdded?.(window.location.pathname);
      await waitForCartUpdate(initialItemCount);
      hideCountdownTimer();

      if (window.refreshCartDrawer) {
        await window.refreshCartDrawer();
      }
      openCartDrawer();
      return;
    }

    // Fallback: add the bundle variant directly via AJAX
    if (selectedBundle.variantId) {
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: selectedBundle.variantId,
            quantity: 1
          })
        });

        if (!response.ok) throw new Error('Failed to add bundle to cart');

        window.markBundleUpsellAdded?.(window.location.pathname);
        hideCountdownTimer();

        setTimeout(async () => {
          if (window.refreshCartDrawer) {
            await window.refreshCartDrawer();
          }
          openCartDrawer();
        }, 300);
      } catch (error) {
        console.error('Error adding bundle to cart:', error);
        alert('Failed to add bundle to cart. Please try again.');
      }
    }
  });

  // "No thanks" button — add just the pack
  addWithoutBtn?.addEventListener('click', async () => {
    const formDataJson = modal.getAttribute('data-form-data');
    if (formDataJson) {
      const formData = JSON.parse(formDataJson);
      closePopup();
      await addToCartNormally(formData);
    } else {
      closePopup();
    }
  });

  async function addToCartNormally(formData) {
    const variantId = formData.id || formData.get?.('id');
    const quantity = formData.quantity || formData.get?.('quantity') || 1;

    if (!variantId) {
      console.error('Variant ID not found');
      return;
    }

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: parseInt(quantity) })
      });

      if (!response.ok) throw new Error('Failed to add to cart');

      setTimeout(async () => {
        if (window.refreshCartDrawer) {
          await window.refreshCartDrawer();
        }
        openCartDrawer();
      }, 300);
    } catch (error) {
      console.error('Error adding to cart:', error);
      alert('Failed to add to cart. Please try again.');
    }
  }
}
